<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-fade-in">
    <div class="bg-white dark:bg-slate-900 w-full max-w-4xl rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[95vh]">

      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-red-50/50 dark:bg-red-900/10">
        <div>
          <h2 class="text-lg font-bold text-red-700 dark:text-red-400 flex items-center gap-2">
            <RotateCcw class="w-5 h-5" />
            {{ form.id ? 'Autorizar Reversión de Desembolso' : 'Solicitar Reversión de Desembolso' }}
          </h2>
          <p class="text-xs text-red-600/70">Esta acción anulará el crédito y creará una transacción negativa en caja.</p>
        </div>
        <button @click="$emit('close')" class="p-2 hover:bg-red-100 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
          <X class="w-5 h-5" />
        </button>
      </div>

      <div class="flex-1 overflow-y-auto p-6 custom-scrollbar">
        <form @submit.prevent="handleSubmit" id="reversionForm" class="space-y-6">

          <div v-if="!form.id" class="bg-slate-50 dark:bg-slate-800/30 p-5 rounded-xl border border-slate-200 dark:border-slate-700">
            <label class="text-xs font-semibold text-slate-500 uppercase mb-2 block">Buscar Crédito Desembolsado (Sin Pagos)</label>
            <div class="relative">
              <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input v-model="loanSearchQuery" @focus="showLoanDropdown = true" type="text" placeholder="Escribe el código del crédito o nombre del cliente..." :class="[inputClasses, 'pl-10']" />
            </div>

            <div v-if="showLoanDropdown && filteredLoans.length > 0" class="absolute z-10 w-full max-w-2xl mt-1 bg-white dark:bg-slate-800 border border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto">
              <div v-for="l in filteredLoans" :key="l.id" @click="selectLoan(l)" class="px-4 py-3 hover:bg-slate-50 cursor-pointer border-b border-slate-100">
                <div class="flex justify-between items-center">
                  <div class="font-bold text-sm">{{ l.customer_name }}</div>
                  <div class="font-mono text-brand-600 font-bold">${{ l.amount }}</div>
                </div>
                <div class="text-[10px] text-slate-500 flex gap-3">
                  <span>EXP: {{ l.code }}</span>
                  <span class="text-emerald-600">Estado: {{ l.status }}</span>
                  <span v-if="l.payments_count > 0" class="text-red-500 font-bold">¡Tiene {{ l.payments_count }} pagos, no se puede revertir!</span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="selectedLoan" class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <h3 class="text-xs font-bold text-slate-400 uppercase mb-4 border-b pb-2">Detalles del Crédito</h3>
              <div class="space-y-3 text-sm">
                <div class="flex justify-between"><span class="text-slate-500">Cliente:</span> <span class="font-bold">{{ selectedLoan.customer_name }}</span></div>
                <div class="flex justify-between"><span class="text-slate-500">Código:</span> <span class="font-mono">{{ selectedLoan.code || selectedLoan.loan_code }}</span></div>
                <div class="flex justify-between"><span class="text-slate-500">Fecha Desembolso:</span> <span>{{ selectedLoan.init_date || selectedLoan.created_at }}</span></div>
                <div class="flex justify-between items-center bg-red-50 p-2 rounded-lg mt-2">
                  <span class="text-red-600 font-bold">Monto a Revertir:</span>
                  <span class="font-mono font-black text-red-700 text-lg">-${{ Number(selectedLoan.amount).toFixed(2) }}</span>
                </div>
              </div>
            </div>

            <div class="space-y-4">
              <div>
                <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Motivo de la Reversión <span class="text-red-500">*</span></label>
                <textarea v-model="form.motive" required rows="4" placeholder="Explique detalladamente por qué se anula este desembolso..." :disabled="form.id && !canAuthorize" :class="inputClasses"></textarea>
              </div>

              <div v-if="canAuthorize" class="bg-amber-50 border border-amber-200 p-4 rounded-xl flex items-start gap-3">
                <input type="checkbox" v-model="form.is_authorized" id="authCheck" class="mt-1 w-4 h-4 text-amber-600 rounded">
                <div>
                  <label for="authCheck" class="font-bold text-amber-800 text-sm cursor-pointer">Autorizar y Ejecutar Reversión Inmediata</label>
                  <p class="text-xs text-amber-700 mt-1">Al marcar esto, se creará una transacción negativa en caja matando el saldo del crédito y devolviendo el crédito a estado CANCELADO/APROBADO.</p>
                </div>
              </div>
            </div>
          </div>

        </form>
      </div>

      <div class="px-6 py-4 bg-white dark:bg-slate-900 border-t border-slate-100 flex justify-end gap-3">
        <button @click="$emit('close')" type="button" class="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50">Cancelar</button>

        <button type="submit" form="reversionForm" :disabled="isSaving || !selectedLoan || (selectedLoan.payments_count > 0)" class="px-6 py-2 text-sm font-bold text-white rounded-xl shadow-lg transition-all flex items-center gap-2 disabled:opacity-50" :class="form.is_authorized ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-500 hover:bg-amber-600'">
          <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
          <RotateCcw v-else class="w-4 h-4" />
          {{ form.is_authorized ? 'Ejecutar Reversión' : 'Solicitar Autorización' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch, computed, onMounted } from 'vue';
import { X, Search, RotateCcw, Loader2 } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import { useToast } from '@/composables/useToast';
import Swal from 'sweetalert2';

const props = defineProps({ show: Boolean, reversionToEdit: Object });
const emit = defineEmits(['close', 'saved']);
const { showSuccess, showError } = useToast();

const inputClasses = "w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20";

const isSaving = ref(false);
const activeLoans = ref([]);
const loanSearchQuery = ref('');
const showLoanDropdown = ref(false);
const selectedLoan = ref(null);

// Lógica de Rol: ¿Puede autorizar directamente?
const canAuthorize = ref(false);

const form = reactive({
  id: null,
  loan_id: '',
  motive: '',
  is_authorized: false
});

onMounted(async () => {
  const user = SecureStorage.getItem('llevo_user');
  if (!user) return;

  // Aquí defines qué roles pueden autorizar directamente (Ej. 1 = Admin)
  canAuthorize.value = user.role_id === 1 || user.role === 'ADMIN';

  // Solo cargamos los créditos si vamos a crear una reversión nueva
  if (!props.reversionToEdit?.id) {
    try {
      // IDEALMENTE: Tu API debe devolver créditos con status=DISBURSED y un contador de pagos = 0
      const res = await apiClient.get(`/loans?company_id=${user.company}&status=ACTIVE`);
      activeLoans.value = res.data.data || res.data;
    } catch (e) { console.error("Error cargando créditos activos"); }
  }
});

const filteredLoans = computed(() => {
  if (!loanSearchQuery.value) return activeLoans.value.slice(0, 5);
  const q = loanSearchQuery.value.toLowerCase();
  return activeLoans.value.filter(l => l.customer_name?.toLowerCase().includes(q) || l.code?.toLowerCase().includes(q));
});

const selectLoan = (loan) => {
  if (loan.remaining_balance <loan.amount) {
    showError("Este crédito ya tiene pagos aplicados. No se puede revertir.");
    return;
  }
  selectedLoan.value = loan;
  form.loan_id = loan.id;
  loanSearchQuery.value = '';
  showLoanDropdown.value = false;
};

watch(() => props.show, (val) => {
  if (val) {
    if (props.reversionToEdit && Object.keys(props.reversionToEdit).length > 0) {
      Object.assign(form, props.reversionToEdit);
      // Para mostrar los datos si estamos editando
      selectedLoan.value = {
        customer_name: form.customer_name,
        code: form.loan_code,
        amount: form.amount,
        payments_count: 0
      };
      form.is_authorized = false; // Reset checkbox por seguridad
    } else {
      form.id = null; form.loan_id = ''; form.motive = ''; form.is_authorized = false;
      selectedLoan.value = null;
    }
  }
});

const handleSubmit = async () => {
  if (!form.loan_id) return showError('Debe seleccionar un crédito válido.');

  const result = await Swal.fire({
    title: form.is_authorized ? '¿Ejecutar Reversión?' : '¿Solicitar Reversión?',
    text: form.is_authorized ? "Esta acción creará un saldo negativo en caja y no se puede deshacer." : "La solicitud pasará a estado Pendiente para ser revisada.",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: form.is_authorized ? '#dc2626' : '#f59e0b',
    cancelButtonText: 'Cancelar',
    confirmButtonText: form.is_authorized ? 'Sí, Revertir Ahora' : 'Enviar Solicitud'
  });

  if (!result.isConfirmed) return;

  isSaving.value = true;
  try {
    const user = SecureStorage.getItem('llevo_user');
    const payload = {
      ...form,
      company_id: user.company,
      requested_by: user.id,
      // Si se marca el check, le indicamos al backend que ejecute de una vez la magia negativa
      action: form.is_authorized ? 'EXECUTE' : 'REQUEST'
    };

    if (form.id) {
      await apiClient.put(`/loan_reversions/${form.id}`, payload);
      showSuccess(form.is_authorized ? 'Reversión Ejecutada Exitosamente' : 'Solicitud Actualizada');
    } else {
      await apiClient.post('/loan_reversions', payload);
      showSuccess(form.is_authorized ? 'Reversión Ejecutada Exitosamente' : 'Solicitud Enviada a Aprobación');
    }
    emit('saved');
    emit('close');
  } catch (e) {
    showError(e.response?.data?.error || 'Error al procesar la reversión.');
  } finally {
    isSaving.value = false;
  }
};
</script>